import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, FileText, Loader2 } from 'lucide-react';
import { vendorApi } from '../../services/api';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import StatusBadge from '../../components/StatusBadge';
import PdfButton from '../../components/PdfButton';
import { paymentStatus } from './Invoices';
import { formatCurrency, formatDate, formatPeriod, isBlankDate } from '../../utils/format';
import { downloadBlob, exportCsv, fileNameFrom } from '../../utils/exportCsv';

/** Breakdown keys from BC (FSCSPO Portal Documents), in the order a settlement is read */
const breakdownRows: { key: string; label: string }[] = [
  { key: 'freight', label: 'Frakt' },
  { key: 'thirdPartyBilling', label: 'Tredjepartsfakturering' },
  { key: 'commission', label: 'Provision' },
  { key: 'fixedCost', label: 'Fasta kostnader' },
  { key: 'expense', label: 'Utgifter och avdrag' },
  { key: 'other', label: 'Övrigt' },
];
const breakdownLabel = (key: string) => breakdownRows.find((r) => r.key === key)?.label || key;

export default function InvoiceDetails() {
  const navigate = useNavigate();
  const { documentType = 'invoice', documentNo = '' } = useParams<{ documentType: string; documentNo: string }>();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [lineFilter, setLineFilter] = useState('');

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => (await vendorApi.me()).data,
  });

  useEffect(() => {
    if (error) navigate('/login');
  }, [error, navigate]);

  const { data, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['vendor', 'invoice', documentType, documentNo],
    queryFn: async () => (await vendorApi.getInvoiceDetails(documentType, documentNo)).data,
    enabled: !!documentNo,
    retry: false,
  });

  const document = data?.document;
  const lines: any[] = document?.lines || [];
  const credit = documentType === 'creditMemo';
  const status = document ? paymentStatus(document) : null;

  const breakdown = useMemo(
    () => breakdownRows.filter((r) => document?.breakdown?.[r.key]).map((r) => ({ ...r, amount: document.breakdown[r.key] as number })),
    [document]
  );
  const lineTypes = useMemo(() => Array.from(new Set(lines.map((l) => l.settlementType))), [lines]);
  const shownLines = lineFilter ? lines.filter((l) => l.settlementType === lineFilter) : lines;

  const downloadPdf = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      const response = await vendorApi.getInvoicePdf(documentType, documentNo);
      downloadBlob(response.data, fileNameFrom(response.headers['content-disposition'], `${documentNo}.pdf`));
    } catch {
      setDownloadError('PDF:en kunde inte skapas. Försök igen om en stund.');
    } finally {
      setDownloading(false);
    }
  };

  const exportLines = () =>
    exportCsv(`avrakning-${documentNo}`, [
      { header: 'Typ', value: (l: any) => breakdownLabel(l.settlementType) },
      { header: 'Transaktion', value: (l: any) => l.transactionNo },
      { header: 'Externt dokumentnr', value: (l: any) => l.externalDocumentNo },
      { header: 'Resurs', value: (l: any) => l.resourceNo },
      { header: 'Beskrivning', value: (l: any) => l.description },
      { header: 'Antal', value: (l: any) => l.quantity },
      { header: 'À-pris', value: (l: any) => l.unitCost },
      { header: 'Belopp exkl. moms', value: (l: any) => l.amount },
      { header: 'Belopp inkl. moms', value: (l: any) => l.amountIncludingVAT },
    ], shownLines);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar logoUrl={vendorData?.tenant?.logoUrl} companyName={vendorData?.tenant?.companyName} />

      <div className="flex flex-1 flex-col">
        <TopBar userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username} />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <Link to="/invoices" className="mb-6 inline-flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-ink">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Alla avräkningar
          </Link>

          {isLoading ? (
            <div className="card p-12 text-center text-gray-500">Laddar avräkningen…</div>
          ) : (loadError as any)?.response?.data?.code === 'BC_SERVICE_MISSING' ? (
            <div className="card p-12 text-center">
              <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden />
              <h1 className="mb-2 text-base font-semibold text-ink">Specifikationen är inte tillgänglig än</h1>
              <p className="mx-auto max-w-[52ch] text-gray-600">
                Business Central behöver uppdateras innan avräkningens rader och PDF kan visas här. Kontakta din åkericentral.
              </p>
            </div>
          ) : isError || !document ? (
            <div className="card p-12 text-center">
              <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden />
              <h1 className="mb-2 text-base font-semibold text-ink">Avräkningen hittades inte</h1>
              <p className="text-gray-600">Den finns inte, eller så hör den inte till ditt konto.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <section className="card mb-6 p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div>
                    <p className="flex flex-wrap items-center gap-2 text-xs">
                      <StatusBadge tone={credit ? 'brass' : 'gray'}>{credit ? 'Kreditnota' : 'Avräkningsfaktura'}</StatusBadge>
                      {status && <StatusBadge tone={status.tone}>{status.label}</StatusBadge>}
                    </p>
                    <h1 className="mt-3 font-display text-[1.35rem] leading-snug text-ink md:text-[1.6rem]">{document.no}</h1>
                    <p className="mt-1 text-gray-600">
                      Period {formatPeriod(document.settlementPeriodStart, document.settlementPeriodEnd)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{credit ? 'Att betala till åkericentralen' : 'Att få utbetalt'}</p>
                    <p className="text-3xl font-semibold tracking-tight tabular-nums text-ink">{formatCurrency(Math.abs(document.amountIncludingVAT))}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      varav moms {formatCurrency(Math.abs(document.vatAmount))}
                    </p>
                    <button
                      type="button"
                      onClick={downloadPdf}
                      disabled={downloading}
                      className="btn-primary mt-4"
                    >
                      {downloading ? 'Skapar PDF…' : 'Ladda ner PDF'}
                      <span className="btn-primary-icon">
                        {downloading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
                      </span>
                    </button>
                    {downloadError && <p role="alert" className="mt-2 text-xs text-red-700">{downloadError}</p>}
                    <div className="mt-2">
                      <PdfButton documentType={documentType} documentNo={document.no} format="excel" />
                    </div>
                  </div>
                </div>

                <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-gray-100 pt-6 text-sm sm:grid-cols-4">
                  {[
                    ['Bokförd', formatDate(document.postingDate)],
                    ['Förfallodatum', formatDate(document.dueDate)],
                    ['Betald', document.closed ? (isBlankDate(document.paidDate) ? 'Ja' : formatDate(document.paidDate)) : 'Nej'],
                    ['Er referens', document.vendorInvoiceNo || '–'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs text-gray-500">{label}</dt>
                      <dd className="mt-0.5 font-medium text-ink">{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                {/* Breakdown */}
                <section aria-labelledby="breakdown-heading" className="card self-start p-6">
                  <h2 id="breakdown-heading" className="text-[15px] font-semibold text-ink">Så räknades det</h2>
                  <dl className="mt-4 divide-y divide-gray-100 text-sm">
                    {breakdown.map((row) => (
                      <div key={row.key} className="flex items-baseline justify-between gap-4 py-2.5">
                        <dt className="text-gray-700">{row.label}</dt>
                        <dd className={`tabular-nums ${row.amount < 0 ? 'text-red-700' : 'text-ink'}`}>{formatCurrency(row.amount)}</dd>
                      </div>
                    ))}
                    <div className="flex items-baseline justify-between gap-4 py-2.5 font-medium">
                      <dt className="text-ink">Summa exkl. moms</dt>
                      <dd className="tabular-nums text-ink">{formatCurrency(document.amount)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 py-2.5">
                      <dt className="text-gray-700">Moms</dt>
                      <dd className="tabular-nums text-ink">{formatCurrency(document.vatAmount)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 pt-3 text-base font-semibold">
                      <dt className="text-ink">Totalt</dt>
                      <dd className="tabular-nums text-ink">{formatCurrency(document.amountIncludingVAT)}</dd>
                    </div>
                  </dl>
                </section>

                {/* Lines */}
                <section aria-labelledby="lines-heading" className="card">
                  <div className="flex flex-wrap items-center justify-between gap-3 p-6 pb-4">
                    <h2 id="lines-heading" className="text-[15px] font-semibold text-ink">
                      Rader <span className="font-normal text-gray-500">({shownLines.length})</span>
                    </h2>
                    <div className="flex flex-wrap items-center gap-2">
                      {lineTypes.length > 1 && (
                        <select
                          aria-label="Visa radtyp"
                          value={lineFilter}
                          onChange={(e) => setLineFilter(e.target.value)}
                          className="rounded-full border border-gray-300 py-1.5 pl-3 pr-8 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Alla rader</option>
                          {lineTypes.map((t) => (
                            <option key={t} value={t}>{breakdownLabel(t)}</option>
                          ))}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={exportLines}
                        className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium text-ink ring-1 ring-inset ring-gray-200 transition-all duration-300 ease-fluid hover:ring-blue-300 active:scale-[0.98]"
                      >
                        <Download className="h-4 w-4" aria-hidden />
                        Excel
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {['Typ', 'Transaktion', 'Beskrivning'].map((h) => (
                            <th key={h} scope="col" className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                          ))}
                          <th scope="col" className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Belopp</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {shownLines.map((line) => (
                          <tr key={line.lineNo}>
                            <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-600">{breakdownLabel(line.settlementType)}</td>
                            <td className="whitespace-nowrap px-5 py-3 text-sm">
                              <span className="font-mono text-gray-900">{line.transactionNo || '–'}</span>
                              {line.externalDocumentNo && <span className="block text-xs text-gray-500">{line.externalDocumentNo}</span>}
                            </td>
                            <td className="px-5 py-3 text-sm text-gray-900">
                              {line.description}
                              {line.resourceNo && <span className="block text-xs text-gray-500">Resurs {line.resourceNo}</span>}
                            </td>
                            <td className={`whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums ${line.amount < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                              {formatCurrency(line.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
